import { useCallback, useEffect, useState } from 'react';
import { api, formatoEuros, type Gasto, type ModoPago, type NotaConGastos } from '../api';
import { Cabecera, Cargando, AvisoError, Confirmacion, Vacio } from '../componentes';
import { useCola, useReferenciaResuelta } from '../hooks';
import { conCache, olvidarDelCache } from '../offline/cache';
import { encolarBorradoDeNota, encolarEnvio } from '../offline/cola';
import { DatosDeLaNota } from './DatosDeLaNota';

/** La copia local de una nota borrada no debe sobrevivirla. */
async function olvidarNotaDelCache(idServidor: number | null): Promise<void> {
  if (idServidor !== null) await olvidarDelCache(`nota:${idServidor}`);
  await olvidarDelCache('notas');
}

export interface GastoPintable extends Gasto {
  /** Id de Gextia, o referencia local si aún no ha subido. */
  ref: string | number;
  pendiente: boolean;
  fotosPendientes: number;
}

export function Nota({
  notaRef,
  nombreInicial,
  alVolver,
  alAnadirGasto,
  alAbrirGasto,
}: {
  notaRef: string | number;
  nombreInicial: string;
  alVolver: () => void;
  alAnadirGasto: (modoPago: ModoPago | null) => void;
  alAbrirGasto: (gasto: GastoPintable) => void;
}) {
  const [nota, setNota] = useState<NotaConGastos | null>(null);
  const [desdeCache, setDesdeCache] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [enviada, setEnviada] = useState(false);
  const [confirmandoBorrado, setConfirmandoBorrado] = useState(false);
  const [confirmandoEnvio, setConfirmandoEnvio] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const cola = useCola();

  // La referencia local se cambia por el id real en cuanto la nota sube; si no,
  // la pantalla nunca pediría sus gastos al servidor y se quedaría vacía en
  // cuanto lo pendiente terminara de subir.
  const refEfectiva = useReferenciaResuelta(notaRef);
  const idServidor = typeof refEfectiva === 'number' ? refEfectiva : null;

  const cargar = useCallback(async () => {
    if (idServidor === null) return; // aún no existe en Gextia
    setError(null);
    try {
      const resultado = await conCache(`nota:${idServidor}`, () => api.nota(idServidor));
      setNota(resultado.datos);
      setDesdeCache(resultado.desdeCache);
    } catch (fallo) {
      setError(fallo);
    }
  }, [idServidor]);

  useEffect(() => {
    void cargar();
  }, [cargar, cola.pendientes]);

  async function enviar() {
    setError(null);
    await encolarEnvio(notaRef);
    setEnviada(true);
    setConfirmandoEnvio(false);
  }

  /**
   * Enviar pide confirmación solo cuando falta el proveedor.
   *
   * Gextia no lo impide por API, pero la factura que sale de la nota lo lleva:
   * sin él, el problema aparece en contabilidad y ya no es de quien lo mandó.
   * Avisar aquí cuesta un toque; descubrirlo allí cuesta una conversación.
   */
  function intentarEnviar() {
    if (nota?.vendorRequired && !nota.vendorId) {
      setConfirmandoEnvio(true);
      return;
    }
    void enviar();
  }

  async function borrar() {
    setBorrando(true);
    setError(null);
    try {
      await encolarBorradoDeNota(notaRef);
      await olvidarNotaDelCache(idServidor);
      alVolver();
    } catch (fallo) {
      setError(fallo);
      setBorrando(false);
      setConfirmandoBorrado(false);
    }
  }

  const gastosLocales = cola.locales.filter(
    (l) => l.tipo === 'gasto' && l.padre === String(notaRef),
  );

  function fotosPendientesDe(gastoRef: string | number): number {
    return cola.locales.filter((l) => l.tipo === 'foto' && l.padre === String(gastoRef)).length;
  }

  const gastos: GastoPintable[] = [
    ...(nota?.gastos ?? []).map((gasto) => ({
      ...gasto,
      ref: gasto.id as string | number,
      pendiente: false,
      fotosPendientes: fotosPendientesDe(gasto.id),
    })),
    ...gastosLocales.map((local) => ({
      id: -1,
      ref: local.idLocal,
      description: local.datos.description as string,
      amount: local.datos.amount as number,
      date: local.datos.date as string,
      categoryId: local.datos.categoryId as number,
      categoryName: local.datos.categoryName as string,
      paymentMode: local.datos.paymentMode as ModoPago,
      photoCount: 0,
      pendiente: true,
      fotosPendientes: fotosPendientesDe(local.idLocal),
    })),
  ];

  if (!nota && idServidor !== null && gastos.length === 0) {
    return (
      <>
        <Cabecera titulo={nombreInicial} alVolver={alVolver} />
        <div className="contenido">
          <AvisoError error={error} />
          {!error && <Cargando />}
        </div>
      </>
    );
  }

  const titulo = nota?.name ?? nombreInicial;
  const yaEnviada = nota?.state !== undefined && nota.state !== 'draft';
  const total = gastos.reduce((suma, gasto) => suma + gasto.amount, 0);
  const modoPago = nota?.paymentMode ?? gastos[0]?.paymentMode ?? null;
  const sinGastos = gastos.length === 0;

  return (
    <>
      <Cabecera
        titulo={titulo}
        alVolver={alVolver}
        accion={
          // La papelera solo cuando hay algo que borrar y se puede: una nota ya
          // enviada no se toca, y una que aún no ha subido se borra sola desde
          // la cola sin pasar por aquí.
          !yaEnviada && idServidor !== null ? (
            <button
              type="button"
              className="boton icono"
              onClick={() => setConfirmandoBorrado(true)}
              aria-label="Borrar la nota"
            >
              🗑
            </button>
          ) : undefined
        }
      />

      {desdeCache && (
        <div className="barra-estado">
          Sin conexión: los gastos ya subidos pueden no estar al día.
        </div>
      )}

      <div className="contenido">
        {enviada && (
          <p className="aviso ok">
            Enviada a aprobar. Si estabas sin cobertura, saldrá en cuanto vuelva.
          </p>
        )}

        {!desdeCache && <AvisoError error={error} />}

        <div className="tarjeta-fila" style={{ padding: '0 0.25rem' }}>
          {/* Lo normal es que lo adelante quien crea la nota, así que solo se
              dice cuando NO es así: un dato que siempre pone lo mismo deja de
              leerse, y entonces tampoco avisa el día que cambia. */}
          <span className="tarjeta-meta">
            {modoPago === 'company_account' ? 'Pagado por la empresa' : ''}
          </span>
          <span className="importe total">{formatoEuros(total)}</span>
        </div>

        {nota && !yaEnviada && <DatosDeLaNota nota={nota} alCambiar={() => void cargar()} />}

        {sinGastos ? (
          <Vacio
            titulo="Todavía no has añadido ningún gasto."
            sugerencia="Empieza por el ticket que tengas en la mano."
          />
        ) : (
          <ul className="lista">
            {gastos.map((gasto) => (
              <li key={String(gasto.ref)}>
                <button
                  type="button"
                  className="tarjeta"
                  onClick={() => alAbrirGasto(gasto)}
                >
                  <div className="tarjeta-fila">
                    <span className="tarjeta-titulo">{gasto.description}</span>
                    <span className="importe">{formatoEuros(gasto.amount)}</span>
                  </div>
                  <div className="tarjeta-meta">
                    <span className="etiqueta">{gasto.categoryName || 'Sin categoría'}</span>{' '}
                    {resumenFotos(gasto)}
                    {gasto.pendiente && ' · '}
                    {gasto.pendiente && <span className="marca espera">pendiente de subir</span>}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}

        {!yaEnviada && gastos.some((g) => g.photoCount + g.fotosPendientes === 0) && (
          <p className="aviso atencion">
            Hay gastos sin foto del ticket. Ábrelos y hazles una.
          </p>
        )}
      </div>

      {!yaEnviada && (
        <div className="acciones-abajo">
          <button
            type="button"
            className="boton ancho"
            onClick={() => alAnadirGasto(modoPago)}
          >
            Añadir gasto
          </button>
          <button
            type="button"
            className="boton secundario ancho"
            onClick={intentarEnviar}
            disabled={sinGastos || enviada}
          >
            Enviar a aprobar
          </button>
        </div>
      )}

      {confirmandoBorrado && (
        <ConfirmarBorrado
          notaId={idServidor}
          nombre={titulo}
          ocupado={borrando}
          alConfirmar={() => void borrar()}
          alCancelar={() => setConfirmandoBorrado(false)}
        />
      )}

      {confirmandoEnvio && (
        <Confirmacion
          titulo="La nota va sin proveedor"
          detalle={
            <p>
              Gextia lo pedirá al contabilizarla, así que alguien tendrá que
              ponerlo después. Puedes añadirlo ahora en los datos de la nota.
            </p>
          }
          textoConfirmar="Enviar igualmente"
          alConfirmar={() => void enviar()}
          alCancelar={() => setConfirmandoEnvio(false)}
        />
      )}
    </>
  );
}

/**
 * Pregunta antes de borrar, diciendo exactamente qué se va.
 *
 * El recuento se pide al servidor en lugar de contar lo que hay en pantalla:
 * puede haber gastos o fotos que esta pantalla no esté mostrando, y prometer
 * "1 gasto" para llevarse tres sería peor que no decir nada.
 */
function ConfirmarBorrado({
  notaId,
  nombre,
  ocupado,
  alConfirmar,
  alCancelar,
}: {
  notaId: number | null;
  nombre: string;
  ocupado: boolean;
  alConfirmar: () => void;
  alCancelar: () => void;
}) {
  const [resumen, setResumen] = useState<{ expenses: number; photos: number } | null>(null);

  useEffect(() => {
    if (notaId === null) return;
    // Si el recuento falla no se bloquea el borrado: se pregunta sin la cifra.
    api
      .resumenBorrado(notaId)
      .then(setResumen)
      .catch(() => setResumen(null));
  }, [notaId]);

  return (
    <Confirmacion
      titulo={`¿Borrar «${nombre}»?`}
      peligrosa
      ocupado={ocupado}
      textoConfirmar="Borrar"
      detalle={
        <>
          <p>
            {resumen && (resumen.expenses > 0 || resumen.photos > 0)
              ? `Se borrarán también ${contar(resumen.expenses, 'gasto', 'gastos')} y ${contar(
                  resumen.photos,
                  'foto',
                  'fotos',
                )}.`
              : 'La nota se borrará con todo lo que tenga dentro.'}
          </p>
          <p className="tarjeta-meta">
            Desaparece también de Gextia, y no se puede deshacer.
          </p>
        </>
      }
      alConfirmar={alConfirmar}
      alCancelar={alCancelar}
    />
  );
}

function contar(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

function resumenFotos(gasto: GastoPintable): string {
  const total = gasto.photoCount + gasto.fotosPendientes;
  if (total === 0) return '· sin fotos';
  const cola = gasto.fotosPendientes > 0 ? ` (${gasto.fotosPendientes} en cola)` : '';
  return `· ${total} ${total === 1 ? 'foto' : 'fotos'}${cola}`;
}
